import { hasStrongTag } from "@/lib/utils/detectTags";
import React from "react";
import { Wysiwyg } from "./Wysiwyg";

export type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5";
export type HeadingStyle = HeadingTag;
/** Lets a caller render the heading as a plain paragraph while keeping semantic style — used only where a visual "headline" must not become a real heading in the document outline (see Hero). */
export type HeadingRenderTag = HeadingTag | "p";
type HeadingWeight = "regular" | "medium";

const styleClasses: Record<HeadingTag, string> = {
  h1: "headline-1",
  h2: "headline-2",
  h3: "headline-3",
  h4: "headline-4",
  h5: "headline-5",
};

const weightClasses: Record<HeadingWeight, string> = {
  regular: "font-weight-regular",
  medium: "font-weight-medium",
};

interface HeadingProps {
  style: HeadingStyle;
  weight?: HeadingWeight;
  children: React.ReactNode;
  className?: string;
  tag?: HeadingRenderTag;
}

export const Heading: React.FC<HeadingProps> = ({
  style,
  tag,
  weight = "medium",
  children,
  className = "",
}) => {
  const Component = tag || style;
  const classes =
    ` ${styleClasses[style]} ${weightClasses[weight]} ${className}`.trim();

  return <Component className={classes}>{children}</Component>;
};

interface WysiwygHeadingProps extends Omit<HeadingProps, "children"> {
  html: string;
}

export const WysiwygHeading: React.FC<WysiwygHeadingProps> = ({
  html,
  weight,
  ...props
}) => {
  const isFormattedBold = hasStrongTag(html);

  return (
    <Heading weight={isFormattedBold ? "regular" : "medium"} {...props}>
      <Wysiwyg html={html} mode="heading" />
    </Heading>
  );
};
